<script setup>
import { useTemplateRef, onBeforeUnmount, computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTrack } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { vOnClickOutside } from '@vueuse/components';
import { CONVERSATION_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import { useConversationFilterContext } from './provider.js';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { removeCondition } from './helper/conditionOrder';

import Button from 'next/button/Button.vue';
import FilterConditionList from './FilterConditionList.vue';
import MultiSelect from 'next/filter/inputs/MultiSelect.vue';
import ColorSelect from 'next/filter/inputs/ColorSelect.vue';
import SingleSelect from 'next/filter/inputs/SingleSelect.vue';

const props = defineProps({
  isFolderView: {
    type: Boolean,
    default: false,
  },
  editable: {
    type: Boolean,
    default: false,
  },
  folderId: {
    type: String,
    default: '',
  },
  folderName: {
    type: String,
    default: '',
  },
  teamIds: {
    type: Array,
    default: () => [],
  },
  parentId: {
    type: [String, Number],
    default: null,
  },
  color: {
    type: String,
    default: '',
  },
  isUpdating: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['applyFilter', 'updateFolder', 'close']);
const { filterTypes } = useConversationFilterContext();
const teams = useMapGetter('teams/getTeams');
const availableParentFolders = useMapGetter(
  'customViews/getAvailableParentFolders'
);

const { t } = useI18n();
const store = useStore();

const SORT_ORDER_ITEMS = Object.freeze([
  'last_activity_at_desc',
  'last_activity_at_asc',
  'last_customer_message_at_desc',
  'last_customer_message_at_asc',
  'created_at_desc',
  'created_at_asc',
  'priority_desc',
  'priority_asc',
  'priority_created_at_desc',
  'priority_created_at_asc',
  'waiting_since_asc',
  'waiting_since_desc',
]);

const filters = defineModel('filters', {
  type: Array,
  default: [],
});
const sortBy = defineModel('sort-by', {
  type: String,
  required: true,
});

const folderNameLocal = ref(props.folderName);
const teamsLocal = ref(
  props.teamIds.map(id => teams.value.find(team => team.id === id))
);
const parentLocal = ref(
  props.parentId
    ? availableParentFolders.value.find(folder => folder.id === props.parentId)
    : null
);
const colorLocal = ref(props.color || '');

const sortOrderOptions = computed(() => {
  return SORT_ORDER_ITEMS.map(value => ({
    id: value,
    name: t(`CHAT_LIST.SORT_ORDER_ITEMS.${value}.TEXT`),
  }));
});

const selectedSortOrder = computed({
  get: () => {
    if (!sortBy.value) return null;
    return (
      sortOrderOptions.value.find(option => option.id === sortBy.value) || null
    );
  },
  set: newValue => {
    sortBy.value = newValue ? newValue.id : 'last_activity_at_desc';
  },
});

const parentFolderOptions = computed(() => {
  const options = [
    {
      id: null,
      name: t('FILTER.NO_PARENT_FOLDER'),
    },
  ];
  const possibleParentFolders = availableParentFolders.value.filter(
    folder => folder.id !== props.folderId
  );
  possibleParentFolders.forEach(folder => {
    options.push({
      id: folder.id,
      name: folder.name,
    });
  });

  return options;
});

const selectedParentFolder = computed({
  get: () => {
    if (!parentLocal.value) {
      return (
        parentFolderOptions.value.find(option => option.id === null) || null
      );
    }
    return (
      parentFolderOptions.value.find(
        option => option.id === parentLocal.value.id
      ) || null
    );
  },
  set: newValue => {
    if (!newValue || newValue.id === null) {
      parentLocal.value = null;
    } else {
      parentLocal.value =
        availableParentFolders.value.find(
          folder => folder.id === newValue.id
        ) || null;
    }
  },
});

const DEFAULT_FILTER = {
  attributeKey: 'status',
  filterOperator: 'equal_to',
  values: [],
  queryOperator: 'and',
};

const resetFilter = () => {
  filters.value = [{ ...DEFAULT_FILTER }];
  colorLocal.value = '';
};

const removeFilter = index => {
  if (filters.value.length === 1) {
    resetFilter();
  } else {
    filters.value = removeCondition(filters.value, index);
  }
};

const addFilter = () => {
  filters.value.push({ ...DEFAULT_FILTER });
};

const conditionListRef = useTemplateRef('conditionListRef');

const isConditionsValid = () => conditionListRef.value.validate();

const updateFilterState = () => {
  store.dispatch(
    'setConversationFilters',
    useSnakeCase(JSON.parse(JSON.stringify(filters.value)))
  );
  store.dispatch('setConversationFiltersSortBy', sortBy.value);
};

const updateSavedCustomViews = () => {
  if (props.isUpdating) return;
  if (isConditionsValid()) {
    updateFilterState();
    emit(
      'updateFolder',
      filters.value,
      sortBy.value,
      folderNameLocal.value,
      teamsLocal.value.map(team => team.id),
      parentLocal.value ? parentLocal.value.id : null,
      colorLocal.value
    );
  }
};

function validateAndSubmit() {
  if (!isConditionsValid()) {
    return;
  }
  updateFilterState();
  emit('applyFilter', filters.value, sortBy.value);
  useTrack(CONVERSATION_EVENTS.APPLY_FILTER, {
    appliedFilters: filters.value.map(filter => ({
      key: filter.attributeKey,
      operator: filter.filterOperator,
      queryOperator: filter.queryOperator,
    })),
  });
}

const filterModalHeaderTitle = computed(() => {
  return !props.isFolderView
    ? t('FILTER.TITLE')
    : t('FILTER.EDIT_CUSTOM_FILTER');
});

onBeforeUnmount(() => emit('close'));
const outsideClickHandler = [
  () => emit('close'),
  {
    ignore: ['#toggleConversationFilterButton', '#toggleAdvancedFilterButton'],
  },
];
</script>

<template>
  <div
    v-on-click-outside="outsideClickHandler"
    class="z-40 max-w-3xl lg:w-[750px] overflow-visible w-full border border-n-weak bg-n-alpha-3 backdrop-blur-[100px] shadow-lg rounded-xl p-6 grid gap-4"
  >
    <h3 class="text-base font-medium leading-6 text-n-slate-12">
      {{ filterModalHeaderTitle }}
    </h3>
    <div v-if="props.isFolderView">
      <label class="border-b border-n-weak pb-2">
        <div class="text-n-slate-11 text-sm mb-2">
          {{ t('FILTER.FOLDER_LABEL') }}
        </div>
        <input
          v-model="folderNameLocal"
          class="py-1.5 px-3 text-n-slate-12 bg-n-alpha-1 text-sm rounded-lg reset-base w-full"
          :placeholder="t('FILTER.INPUT_PLACEHOLDER')"
        />
      </label>
    </div>
    <FilterConditionList
      ref="conditionListRef"
      v-model:filters="filters"
      :filter-types="filterTypes"
      @remove="removeFilter"
    />

    <div>
      <label class="border-b border-n-weak pb-2">
        <div class="text-n-slate-11 text-sm mb-2">
          {{ $t('FILTER.SORT_BY_LABEL') }}
        </div>
        <SingleSelect
          v-model="selectedSortOrder"
          :options="sortOrderOptions"
          disable-search
        />
      </label>
    </div>

    <div v-if="props.isFolderView" class="grid grid-cols-3 gap-4">
      <label class="border-b border-n-weak pb-2">
        <div class="text-n-slate-11 text-sm mb-2">
          {{ t('FILTER.TEAMS_LABEL') }}
        </div>
        <MultiSelect v-model="teamsLocal" :options="teams" max-chips="1" />
      </label>
      <label class="border-b border-n-weak pb-2">
        <div class="text-n-slate-11 text-sm mb-2">
          {{ t('FILTER.PARENT_FOLDER_LABEL') }}
        </div>
        <SingleSelect
          v-model="selectedParentFolder"
          :options="parentFolderOptions"
          disable-search
        />
      </label>

      <label class="border-b border-n-weak pb-2">
        <div class="text-n-slate-11 text-sm mb-2">
          {{ t('FILTER.COLOR_LABEL') }}
        </div>
        <ColorSelect v-model="colorLocal" />
      </label>
    </div>

    <div v-if="!isFolderView || editable" class="flex gap-2 justify-between">
      <Button sm ghost blue @click="addFilter">
        {{ $t('FILTER.ADD_NEW_FILTER') }}
      </Button>
      <div class="flex gap-2">
        <Button sm faded slate @click="resetFilter">
          {{ t('FILTER.CLEAR_BUTTON_LABEL') }}
        </Button>
        <template v-if="isFolderView">
          <Button
            v-if="editable"
            sm
            solid
            blue
            :disabled="!folderNameLocal || isUpdating"
            :is-loading="isUpdating"
            @click="updateSavedCustomViews"
          >
            {{ t('FILTER.UPDATE_BUTTON_LABEL') }}
          </Button>
        </template>
        <Button v-else sm solid blue @click="validateAndSubmit">
          {{ t('FILTER.SUBMIT_BUTTON_LABEL') }}
        </Button>
      </div>
    </div>
  </div>
</template>
